import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2, Tv, Play, Pause, ChevronLeft, ChevronRight, X, AlertCircle, RadioTower } from "lucide-react";
import { NexSysMark } from "./ui";
import { gateApi, getGateToken } from "./gate";
import { nexsysApi } from "./api";
import { NAV } from "./data";
import { Overview, RiskView, TimelineView, DigitalTwinView, PredictiveView, GovernanceView, SignalsView } from "./views";

const TOKEN_KEY = "nexsys_gate_token";
const ROTATE_MS = 10000;

// Curated, visual-only rotation for a hands-off TV loop (skips interactive/admin screens).
const KIOSK_VIEWS = ["command", "risk", "timeline", "twin", "predictive", "governance", "signals"];

function KioskShell({ children }) {
  return (
    <div className="nx-root fixed inset-0 flex items-center justify-center overflow-hidden bg-[#05070f] px-6" data-testid="kiosk-pair">
      <div className="pointer-events-none absolute inset-0 nx-grid-bg opacity-40" />
      <div className="pointer-events-none absolute left-1/2 top-1/3 h-[520px] w-[520px] -translate-x-1/2 rounded-full bg-cyan-500/10 blur-[140px]" />
      <motion.div
        initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        className="relative z-10 w-full max-w-[560px] rounded-3xl border border-white/10 bg-white/[0.03] p-10 text-center backdrop-blur-xl"
      >
        {children}
      </motion.div>
    </div>
  );
}

function PairScreen({ onPaired }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const inputRef = useRef(null);

  useEffect(() => { setTimeout(() => inputRef.current?.focus(), 200); }, []);

  const claim = async (e) => {
    e?.preventDefault();
    const c = code.trim();
    if (c.length < 6) return;
    setBusy(true); setError("");
    try {
      const r = await gateApi.pairClaim(c);
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { setError(typeof d.detail === "string" ? d.detail : "Could not pair. Try again."); setBusy(false); return; }
      localStorage.setItem(TOKEN_KEY, d.token);
      window.dispatchEvent(new Event("nexsys-gate-change"));
      onPaired();
    } catch {
      setError("Network error. Please try again.");
      setBusy(false);
    }
  };

  return (
    <KioskShell>
      <div className="flex flex-col items-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-cyan-400/10">
          <Tv className="h-8 w-8 text-cyan-300" />
        </div>
        <div className="mt-5 font-display text-3xl font-bold tracking-tight">
          <span className="text-white">Nex</span><span className="text-cyan-400">Sys</span>
          <span className="ml-2 text-white/60">Presenter</span>
        </div>
        <p className="mt-3 max-w-sm text-[15px] leading-relaxed text-white/50">
          On your computer, open the Command Center and press <span className="text-cyan-300">Cast / Present</span> to
          generate a pairing code. Enter it below to display the dashboard on this screen.
        </p>
      </div>

      <form onSubmit={claim} className="mt-8 space-y-4">
        <input
          ref={inputRef}
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
          inputMode="numeric"
          placeholder="000000"
          className="w-full rounded-2xl border border-white/10 bg-black/40 px-4 py-5 text-center font-mono text-4xl tracking-[0.5em] text-white placeholder:text-white/20 outline-none focus:border-cyan-400/50"
          data-testid="kiosk-code-input"
        />
        {error && (
          <div className="flex items-center justify-center gap-2 rounded-lg border border-red-400/30 bg-red-500/10 px-3 py-2.5 text-[13px] text-red-300" data-testid="kiosk-pair-error">
            <AlertCircle className="h-4 w-4 shrink-0" /> {error}
          </div>
        )}
        <button
          type="submit" disabled={busy || code.trim().length < 6}
          className="flex w-full items-center justify-center gap-2 rounded-2xl bg-cyan-500 px-4 py-4 text-lg font-semibold text-black transition-opacity hover:bg-cyan-400 disabled:opacity-50"
          data-testid="kiosk-pair-submit"
        >
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <RadioTower className="h-5 w-5" />} Pair & present
        </button>
      </form>
    </KioskShell>
  );
}

function KioskDashboard() {
  const [idx, setIdx] = useState(0);
  const [rotating, setRotating] = useState(true);
  const [progress, setProgress] = useState(0);
  const [showControls, setShowControls] = useState(true);
  const [time, setTime] = useState(new Date());

  const [portfolio, setPortfolio] = useState(null);
  const [insights, setInsights] = useState(null);
  const [timeseries, setTimeseries] = useState([]);

  const viewId = KIOSK_VIEWS[idx];
  const label = NAV.find((n) => n.id === viewId)?.label || viewId;
  const hideTimer = useRef(null);

  // live data (same source as Command Center)
  const loadLive = useCallback(async () => {
    try {
      const [p, ins] = await Promise.all([nexsysApi.portfolio(), nexsysApi.insights()]);
      setPortfolio(p); setInsights(ins);
    } catch { /* token expired / offline: views degrade gracefully */ }
  }, []);
  useEffect(() => {
    document.title = "NexSys Presenter — Nexis.team";
    loadLive();
    nexsysApi.timeseries().then((d) => setTimeseries(d.series || [])).catch(() => {});
    const poll = setInterval(loadLive, 30000);
    return () => clearInterval(poll);
  }, [loadLive]);

  // keep the kiosk session alive (heartbeat) so an admin can still see/kick it
  useEffect(() => {
    const beat = () => gateApi.heartbeat().catch(() => {});
    beat();
    const t = setInterval(beat, 20000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => { const t = setInterval(() => setTime(new Date()), 1000); return () => clearInterval(t); }, []);

  const go = useCallback((dir) => {
    setIdx((i) => (i + dir + KIOSK_VIEWS.length) % KIOSK_VIEWS.length);
    setProgress(0);
  }, []);

  // auto-rotate + progress bar
  useEffect(() => {
    if (!rotating) return;
    setProgress(0);
    const step = 100;
    const tick = setInterval(() => setProgress((p) => Math.min(100, p + (step / ROTATE_MS) * 100)), step);
    const next = setTimeout(() => go(1), ROTATE_MS);
    return () => { clearInterval(tick); clearTimeout(next); };
  }, [rotating, idx, go]);

  // auto-hide controls after inactivity (TV-friendly)
  const wake = useCallback(() => {
    setShowControls(true);
    clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(() => setShowControls(false), 4000);
  }, []);
  useEffect(() => { wake(); return () => clearTimeout(hideTimer.current); }, [wake]);

  const exit = () => {
    const url = new URL(window.location.href);
    url.searchParams.delete("kiosk");
    window.location.href = url.pathname;
  };

  const viewEl = useMemo(() => {
    switch (viewId) {
      case "risk": return <RiskView runCommand={() => {}} />;
      case "timeline": return <TimelineView />;
      case "twin": return <DigitalTwinView />;
      case "predictive": return <PredictiveView runCommand={() => {}} />;
      case "governance": return <GovernanceView />;
      case "signals": return <SignalsView insights={insights} />;
      default: return <Overview portfolio={portfolio} timeseries={timeseries} insights={insights} runCommand={() => {}} />;
    }
  }, [viewId, portfolio, timeseries, insights]);

  return (
    <div
      className="nx-root fixed inset-0 flex flex-col overflow-hidden bg-[#05070f]"
      onMouseMove={wake} onClick={wake} data-testid="kiosk-dashboard"
    >
      <div className="pointer-events-none absolute inset-0 nx-grid-bg opacity-30" />

      {/* rotation progress */}
      <div className="absolute inset-x-0 top-0 z-30 h-1 bg-white/5">
        <div className="h-full bg-gradient-to-r from-cyan-400 to-indigo-500 transition-[width] duration-100 ease-linear" style={{ width: `${rotating ? progress : 0}%` }} />
      </div>

      {/* header */}
      <header className="relative z-20 flex shrink-0 items-center gap-5 px-10 py-6">
        <NexSysMark size={44} />
        <div className="leading-tight">
          <div className="font-mono text-[13px] tracking-[0.3em] text-cyan-400/70">NEXSYS PRESENTER</div>
          <AnimatePresence mode="wait">
            <motion.div
              key={viewId}
              initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.3 }}
              className="font-display text-3xl font-bold text-white"
              data-testid="kiosk-view-label"
            >
              {label}
            </motion.div>
          </AnimatePresence>
        </div>
        <div className="ml-auto flex items-center gap-6">
          <div className="flex items-center gap-2">
            {KIOSK_VIEWS.map((v, i) => (
              <span key={v} className={`h-2 rounded-full transition-all ${i === idx ? "w-8 bg-cyan-400" : "w-2 bg-white/20"}`} />
            ))}
          </div>
          <div className="font-mono text-2xl tabular-nums text-white/70">{time.toLocaleTimeString([], { hour12: false })}</div>
        </div>
      </header>

      {/* scaled-up content */}
      <main className="nx-scroll relative z-10 flex-1 overflow-hidden px-8 pb-8">
        <div className="h-full w-full overflow-hidden" style={{ zoom: 1.35 }}>
          <AnimatePresence mode="wait">
            <motion.div
              key={viewId}
              initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 1.01 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            >
              {viewEl}
            </motion.div>
          </AnimatePresence>
        </div>
      </main>

      {/* floating controls */}
      <AnimatePresence>
        {showControls && (
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 20 }}
            className="fixed inset-x-0 bottom-6 z-30 flex justify-center"
          >
            <div className="flex items-center gap-2 rounded-2xl border border-white/10 bg-black/70 px-3 py-2.5 backdrop-blur-xl">
              <button onClick={() => go(-1)} data-testid="kiosk-prev" className="flex h-11 w-11 items-center justify-center rounded-xl text-white/70 hover:bg-white/10 hover:text-white">
                <ChevronLeft className="h-6 w-6" />
              </button>
              <button
                onClick={() => setRotating((r) => !r)} data-testid="kiosk-rotate-toggle"
                className={`flex h-11 items-center gap-2 rounded-xl px-4 text-sm font-semibold ${rotating ? "bg-cyan-500 text-black" : "border border-white/15 text-white/80 hover:bg-white/10"}`}
              >
                {rotating ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5" />}
                {rotating ? "Auto-rotating" : "Paused"}
              </button>
              <button onClick={() => go(1)} data-testid="kiosk-next" className="flex h-11 w-11 items-center justify-center rounded-xl text-white/70 hover:bg-white/10 hover:text-white">
                <ChevronRight className="h-6 w-6" />
              </button>
              <div className="mx-1 h-7 w-px bg-white/10" />
              <button onClick={exit} data-testid="kiosk-exit" className="flex h-11 items-center gap-2 rounded-xl px-4 text-sm text-white/60 hover:bg-red-500/15 hover:text-red-300">
                <X className="h-5 w-5" /> Exit
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function KioskMode() {
  const [status, setStatus] = useState("checking"); // checking | pair | ready

  const verify = useCallback(() => {
    if (!getGateToken()) { setStatus("pair"); return; }
    gateApi.me()
      .then((r) => { setStatus(r.ok ? "ready" : "pair"); if (!r.ok) localStorage.removeItem(TOKEN_KEY); })
      .catch(() => setStatus("pair"));
  }, []);

  useEffect(() => { verify(); }, [verify]);

  if (status === "checking") {
    return <KioskShell><div className="flex items-center justify-center gap-3 py-4 text-white/60"><Loader2 className="h-6 w-6 animate-spin text-cyan-300" /> Verifying presenter session…</div></KioskShell>;
  }
  if (status === "pair") return <PairScreen onPaired={() => setStatus("ready")} />;
  return <KioskDashboard />;
}
